// Logos de transportistas como componentes SVG
// Estilo similar a Outvio

export function MRWLogo({ className = "h-6" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 80 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text x="0" y="18" fontFamily="Arial Black, sans-serif" fontSize="16" fontWeight="900" fill="#E30613">MR</text>
      <text x="30" y="18" fontFamily="Arial Black, sans-serif" fontSize="16" fontWeight="900" fontStyle="italic" fill="#E30613">W</text>
    </svg>
  )
}

export function GLSLogo({ className = "h-6" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 70 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text x="0" y="18" fontFamily="Arial Black, sans-serif" fontSize="18" fontWeight="900" fill="#1B3F8B">GLS</text>
      <text x="42" y="18" fontFamily="Arial, sans-serif" fontSize="10" fontWeight="400" fill="#FFC20E">.</text>
    </svg>
  )
}

export function CTTLogo({ className = "h-6" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 90 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text x="0" y="18" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" fill="#E30613">CTT</text>
      <text x="30" y="18" fontFamily="Arial, sans-serif" fontSize="10" fontWeight="400" fill="#333">express</text>
    </svg>
  )
}

export function DHLLogo({ className = "h-6" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 80 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="2" width="80" height="20" rx="3" fill="#FFCC00" />
      <text x="8" y="17" fontFamily="Arial Black, sans-serif" fontSize="14" fontWeight="900" fill="#D40511">DHL</text>
    </svg>
  )
}

// Componente que muestra el logo apropiado según el código de transportista
export function CarrierLogo({
  carrier,
  className = "h-6 w-20"
}: {
  carrier: string
  className?: string
}) {
  const carrierLower = carrier.toLowerCase()

  if (carrierLower.includes('mrw')) {
    return <MRWLogo className={className} />
  }
  if (carrierLower.includes('gls')) {
    return <GLSLogo className={className} />
  }
  if (carrierLower.includes('ctt')) {
    return <CTTLogo className={className} />
  }
  if (carrierLower.includes('dhl')) {
    return <DHLLogo className={className} />
  }

  // Fallback para transportistas desconocidos - mostrar iniciales
  const initials = carrier.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase()
  return (
    <span className={`${className} inline-flex items-center justify-center text-xs font-bold text-slate-500 bg-slate-100 dark:bg-slate-700 dark:text-slate-400 rounded px-1`}>
      {initials || '?'}
    </span>
  )
}

// Versión compacta del logo para tablas
export function CarrierBadge({
  carrierName,
  className = ""
}: {
  carrierName: string | undefined
  className?: string
}) {
  if (!carrierName) {
    return <span className="text-slate-300 dark:text-slate-600">—</span>
  }

  const carrierLower = carrierName.toLowerCase()

  // Detectar transportista y mostrar badge con color
  if (carrierLower.includes('mrw')) {
    return (
      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded ${className}`}>
        MRW
      </span>
    )
  }
  if (carrierLower.includes('gls')) {
    return (
      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded ${className}`}>
        GLS
      </span>
    )
  }
  if (carrierLower.includes('ctt')) {
    return (
      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded ${className}`}>
        CTT
      </span>
    )
  }
  if (carrierLower.includes('dhl')) {
    return (
      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded ${className}`}>
        DHL
      </span>
    )
  }
  if (carrierLower.includes('correos')) {
    return (
      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded ${className}`}>
        COR
      </span>
    )
  }
  if (carrierLower.includes('seur')) {
    return (
      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded ${className}`}>
        SEUR
      </span>
    )
  }

  // Fallback - mostrar primeras 3-4 letras
  const short = carrierName.replace(/[^a-zA-Z]/g, '').slice(0, 4).toUpperCase()
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 rounded ${className}`} title={carrierName}>
      {short || '?'}
    </span>
  )
}

// Badge de forma de pago
export function PaymentBadge({
  paymentMethod,
  className = ""
}: {
  paymentMethod: string | undefined
  className?: string
}) {
  if (!paymentMethod) {
    return <span className="text-slate-300 dark:text-slate-600">—</span>
  }

  const methodLower = paymentMethod.toLowerCase()

  // PayPal - Azul característico de PayPal
  if (methodLower.includes('paypal')) {
    return (
      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-[#0070ba] text-white rounded ${className}`}>
        PayPal
      </span>
    )
  }
  // Tarjeta / Card - Púrpura
  if (methodLower.includes('tarjeta') || methodLower.includes('card') || methodLower.includes('stripe') || methodLower.includes('redsys')) {
    return (
      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-purple-600 text-white rounded ${className}`}>
        Tarjeta
      </span>
    )
  }
  // Transferencia bancaria - Verde
  if (methodLower.includes('transfer') || methodLower.includes('banco') || methodLower.includes('bank')) {
    return (
      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-emerald-600 text-white rounded ${className}`}>
        Transf.
      </span>
    )
  }
  // Contrareembolso / COD - Naranja (destaca porque requiere acción)
  // Incluye variantes: español (contrareembolso), portugués (cobrança, à cobrança), inglés (COD, cash on delivery)
  if (methodLower.includes('reembolso') || methodLower.includes('cobran') || methodLower.includes('cod') || methodLower.includes('cash') || methodLower.includes('contra') || methodLower.includes('entrega')) {
    return (
      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-orange-500 text-white rounded ${className}`}>
        C.Reemb
      </span>
    )
  }
  // Bizum - Color corporativo Bizum
  if (methodLower.includes('bizum')) {
    return (
      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-[#00c6c6] text-white rounded ${className}`}>
        Bizum
      </span>
    )
  }

  // Fallback - Gris con texto abreviado
  const short = paymentMethod.replace(/[^a-zA-Z]/g, '').slice(0, 5)
  return (
    <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium bg-slate-500 text-white rounded ${className}`} title={paymentMethod}>
      {short || '?'}
    </span>
  )
}

// Badge VIES - Cliente intracomunitario con VAT válido
// IMPORTANTE: Solo se considera VIES si hay un vat_number válido (no el dni)
// El campo dni puede contener NIFs locales con prefijo de país que NO son intracomunitarios
export function ViesBadge({
  dni,
  vatNumber: vatNumberProp,
  countryCode,
  className = ""
}: {
  dni: string | undefined
  vatNumber?: string | undefined
  countryCode: string
  className?: string
}) {
  // Países UE para VIES intracomunitario (excepto ES que es origen)
  const EU_COUNTRIES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'FI', 'FR', 'GR', 'EL', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK']

  // Prefijos VAT válidos de la UE
  const VAT_PREFIXES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK']

  const country = countryCode?.toUpperCase()

  // Solo usar vat_number para determinar VIES (NO el dni)
  const vatNumber = vatNumberProp?.toUpperCase().replace(/[^A-Z0-9]/g, '') || ''

  // Verificar si es país UE
  const isEUCountry = EU_COUNTRIES.includes(country)

  // Solo es intracomunitario si hay un vat_number válido con formato VAT europeo
  const hasValidVat = vatNumber.length >= 8 && VAT_PREFIXES.some(prefix => vatNumber.startsWith(prefix))

  if (hasValidVat) {
    return (
      <span
        className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-blue-600 text-white rounded ${className}`}
        title={`VAT: ${vatNumberProp} - Cliente intracomunitario`}
      >
        VIES
      </span>
    )
  }

  // Si es país UE pero sin VAT válido, mostrar indicador EU
  if (isEUCountry) {
    return (
      <span
        className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium bg-slate-400 text-white rounded ${className}`}
        title={dni ? `NIF: ${dni} - País UE sin VAT válido` : 'País UE - Sin NIF'}
      >
        EU
      </span>
    )
  }

  // No es país UE - no mostrar nada
  return null
}

// Banderas de país
export function CountryFlag({
  countryCode,
  className = "w-5 h-4"
}: {
  countryCode: string
  className?: string
}) {
  const code = countryCode.toUpperCase()

  // Emoji flags - todos los países UE + UK + otros comunes
  const flags: Record<string, string> = {
    // Principales
    ES: '🇪🇸', PT: '🇵🇹', FR: '🇫🇷', DE: '🇩🇪', IT: '🇮🇹', GB: '🇬🇧',
    // Benelux
    BE: '🇧🇪', NL: '🇳🇱', LU: '🇱🇺',
    // Nórdicos
    FI: '🇫🇮', SE: '🇸🇪', DK: '🇩🇰', NO: '🇳🇴',
    // Europa Central
    AT: '🇦🇹', CH: '🇨🇭', PL: '🇵🇱', CZ: '🇨🇿', HU: '🇭🇺', SK: '🇸🇰',
    // Europa del Este
    RO: '🇷🇴', BG: '🇧🇬', HR: '🇭🇷', SI: '🇸🇮',
    // Bálticos
    LT: '🇱🇹', LV: '🇱🇻', EE: '🇪🇪',
    // Islas Británicas
    IE: '🇮🇪',
    // Sur de Europa
    GR: '🇬🇷', CY: '🇨🇾', MT: '🇲🇹', AD: '🇦🇩',
    // Otros
    US: '🇺🇸',
  }

  return (
    <span className={`${className} inline-flex items-center justify-center text-base`} title={code}>
      {flags[code] || '🏳️'}
    </span>
  )
}

// Badge de estado con colores estilo Outvio
export function StatusBadge({
  status,
  className = ""
}: {
  status: string
  className?: string
}) {
  const statusConfig: Record<string, { label: string; color: string; barColor: string }> = {
    pending: { label: 'PENDIENTE', color: 'text-slate-600 bg-slate-100', barColor: 'bg-slate-400' },
    created: { label: 'CREADO', color: 'text-blue-600 bg-blue-50', barColor: 'bg-blue-400' },
    shipped: { label: 'ENVIADO', color: 'text-indigo-600 bg-indigo-50', barColor: 'bg-indigo-400' },
    in_transit: { label: 'EN TRÁNSITO', color: 'text-teal-600 bg-teal-50', barColor: 'bg-teal-500' },
    out_for_delivery: { label: 'EN REPARTO', color: 'text-amber-600 bg-amber-50', barColor: 'bg-amber-500' },
    delivered: { label: 'ENTREGADO', color: 'text-emerald-600 bg-emerald-50', barColor: 'bg-emerald-500' },
    failed: { label: 'FALLIDO', color: 'text-red-600 bg-red-50', barColor: 'bg-red-500' },
    returned: { label: 'DEVUELTO', color: 'text-orange-600 bg-orange-50', barColor: 'bg-orange-500' },
    cancelled: { label: 'CANCELADO', color: 'text-slate-500 bg-slate-100', barColor: 'bg-slate-400' },
    recogido: { label: 'RECOGIDO', color: 'text-cyan-600 bg-cyan-50', barColor: 'bg-cyan-500' },
  }

  const config = statusConfig[status] || statusConfig.pending

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${config.color}`}>
        {config.label}
      </span>
      {/* Barra de color lateral estilo Outvio */}
      <div className={`w-1 h-8 rounded-full ${config.barColor}`} />
    </div>
  )
}

// Indicador de conexión de transportista
export function CarrierConnectionDot({ connected }: { connected: boolean }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${
        connected ? 'bg-emerald-500' : 'bg-red-500'
      }`}
      title={connected ? 'Conectado' : 'Desconectado'}
    />
  )
}
